import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/apiClient";
import type { Location, ShopifyPickupOptions } from "../api/types";
import { EmptyState } from "../components/ui/EmptyState";
import { countLine } from "../components/locations/shopLabels";
import { ShopSheet } from "../components/locations/ShopSheet";
import { ShopTile } from "../components/locations/ShopTile";
import { businessToday } from "../utils/businessDay";
import type { DashboardOutletContext } from "./dashboardOutletContext";

/**
 * G-203 redesign (2026-11-03) — shops as tiles, one panel per shop. The page
 * says what each shop is doing now (open, next closure, door pin, Shopify) and
 * every change is made in the panel, section by section. "New location" opens
 * the same panel empty. Kit only: no inline styles, no native dialogs.
 */
export function LocationManagePage() {
  const { refreshLocations } = useOutletContext<DashboardOutletContext>();
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [pickup, setPickup] = useState<ShopifyPickupOptions | null>(null);
  const [pickupError, setPickupError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [tick, setTick] = useState(() => Date.now());

  const loadAll = useCallback(async () => {
    setListError(null);
    try {
      setAllLocations(await api.listLocations({ includeInactive: true }));
    } catch (err: unknown) {
      setListError(err instanceof Error ? err.message : "Could not load locations");
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadPickup = useCallback(async () => {
    try {
      setPickup(await api.listShopifyPickupLocations());
      setPickupError(null);
    } catch (err: unknown) {
      setPickupError(err instanceof Error ? err.message : "Could not load Shopify pickup locations");
    }
  }, []);

  useEffect(() => {
    void loadAll();
    void loadPickup();
  }, [loadAll, loadPickup]);

  // The "open now" pills follow the clock, once a minute.
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const now = useMemo(() => new Date(tick), [tick]);
  const today = useMemo(() => businessToday(now).ymd, [now]);

  const afterMutation = useCallback(async () => {
    await loadAll();
    await loadPickup();
    await refreshLocations();
  }, [loadAll, loadPickup, refreshLocations]);

  const selected = selectedId ? allLocations.find((l) => l.id === selectedId) ?? null : null;
  const sorted = useMemo(
    () => [...allLocations].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name)),
    [allLocations],
  );

  return (
    <section className="page-stack shops-page">
      <header className="shops-head">
        <div>
          <h2 className="page-title">Locations</h2>
          <p className="page-subtitle">{listLoading ? "Loading…" : countLine(allLocations)}</p>
        </div>
        <button type="button" className="button" onClick={() => { setSelectedId(null); setCreating(true); }}>
          New location
        </button>
      </header>

      {listError ? <div className="alert error">{listError}</div> : null}
      {pickupError ? <div className="alert error">{pickupError}</div> : null}

      {!listLoading && sorted.length === 0 ? (
        <EmptyState title="No locations yet" />
      ) : (
        <div className="shop-tiles">
          {sorted.map((loc) => (
            <ShopTile
              key={loc.id}
              location={loc}
              pickup={pickup}
              now={now}
              today={today}
              selected={loc.id === selectedId}
              onOpen={(l) => { setCreating(false); setSelectedId(l.id); }}
            />
          ))}
        </div>
      )}

      {selected || creating ? (
        <ShopSheet
          location={creating ? null : selected}
          pickup={pickup}
          now={now}
          today={today}
          onClose={() => { setSelectedId(null); setCreating(false); }}
          onChanged={afterMutation}
          onCreated={async (created) => {
            await afterMutation();
            setCreating(false);
            setSelectedId(created.id);
          }}
        />
      ) : null}
    </section>
  );
}
